<script setup>
import { computed, nextTick, ref } from 'vue'
import { useI18n } from '../../i18n'
import {
  isReceiptDateValid,
  isReceiptTimeValid,
  maskReceiptDate,
  maskReceiptTime,
} from './receiptDateTime.js'

const props = defineProps({
  id: { type: String, required: true },
  label: { type: String, required: true },
  modelValue: { type: Object, required: true },
  disabled: Boolean,
  invalid: Boolean,
  datePickerLabel: { type: String, required: true },
  timePickerLabel: { type: String, required: true },
})
const emit = defineEmits(['update:modelValue'])
const { t } = useI18n()
const datePicker = ref(null)
const timePickerButton = ref(null)
const hourSelect = ref(null)
const timePickerOpen = ref(false)
const selectedHour = ref('00')
const selectedMinute = ref('00')
const selectedSecond = ref('00')

const hours = Array.from({ length: 24 }, (_, value) => String(value).padStart(2, '0'))
const minutes = Array.from({ length: 60 }, (_, value) => String(value).padStart(2, '0'))
const dateInvalid = computed(() => props.modelValue.date?.length === 10 && !isReceiptDateValid(props.modelValue.date))
const timeInvalid = computed(() => props.modelValue.time?.length === 8 && !isReceiptTimeValid(props.modelValue.time))
const localInvalid = computed(() => dateInvalid.value || timeInvalid.value)
const fieldInvalid = computed(() => props.invalid || localInvalid.value)
const selectedTime = computed(() => `${selectedHour.value}:${selectedMinute.value}:${selectedSecond.value}`)

function update(key, value) {
  emit('update:modelValue', { ...props.modelValue, [key]: value })
}

function updateMasked(event, key, mask) {
  const value = mask(event.target.value)
  event.target.value = value
  update(key, value)
}

function applyNativeDate(event) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(event.target.value)
  if (match) update('date', `${match[3]}.${match[2]}.${match[1]}`)
}

function openDatePicker() {
  if (typeof datePicker.value?.showPicker === 'function') datePicker.value.showPicker()
  else datePicker.value?.click()
}

async function toggleTimePicker() {
  if (timePickerOpen.value) {
    closeTimePicker()
    return
  }
  const match = /^(\d{2}):(\d{2}):(\d{2})$/.exec(props.modelValue.time ?? '')
  selectedHour.value = match && Number(match[1]) <= 23 ? match[1] : '00'
  selectedMinute.value = match && Number(match[2]) <= 59 ? match[2] : '00'
  selectedSecond.value = match && Number(match[3]) <= 59 ? match[3] : '00'
  timePickerOpen.value = true
  await nextTick()
  hourSelect.value?.focus()
}

function closeTimePicker(restoreFocus = true) {
  timePickerOpen.value = false
  if (restoreFocus) nextTick(() => timePickerButton.value?.focus())
}

function applySelectedTime() {
  update('time', selectedTime.value)
  closeTimePicker()
}
</script>

<template>
  <fieldset class="localized-date-time" :class="{ invalid: fieldInvalid }">
    <legend>{{ label }}</legend>

    <div class="localized-date-time-field">
      <label class="sr-only" :for="`${id}-date`">{{ t('dateInputFormat') }}</label>
      <div class="localized-date-time-input">
        <input
          :id="`${id}-date`"
          :value="modelValue.date"
          inputmode="numeric"
          autocomplete="off"
          placeholder="dd.mm.yyyy"
          maxlength="10"
          :disabled="disabled"
          :aria-invalid="invalid || dateInvalid"
          :aria-describedby="localInvalid ? `${id}-format-error` : undefined"
          @input="updateMasked($event, 'date', maskReceiptDate)"
        >
        <button type="button" :disabled="disabled" :aria-label="datePickerLabel" @click="openDatePicker">
          <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M7 2v3m10-3v3M4 9h16M5 4h14a1 1 0 0 1 1 1v15H4V5a1 1 0 0 1 1-1Z" /></svg>
        </button>
        <input ref="datePicker" class="native-picker" type="date" tabindex="-1" aria-hidden="true" :disabled="disabled" @change="applyNativeDate">
      </div>
    </div>

    <div class="localized-date-time-field">
      <label class="sr-only" :for="`${id}-time`">{{ t('timeInputFormat') }}</label>
      <div class="localized-date-time-input">
        <input
          :id="`${id}-time`"
          :value="modelValue.time"
          inputmode="numeric"
          autocomplete="off"
          placeholder="HH:mm:ss"
          maxlength="8"
          :disabled="disabled"
          :aria-invalid="invalid || timeInvalid"
          :aria-describedby="localInvalid ? `${id}-format-error` : undefined"
          @input="updateMasked($event, 'time', maskReceiptTime)"
        >
        <button
          ref="timePickerButton"
          type="button"
          :disabled="disabled"
          :aria-label="timePickerLabel"
          :aria-expanded="timePickerOpen"
          :aria-controls="`${id}-time-picker`"
          @click.stop="toggleTimePicker"
        >
          <svg aria-hidden="true" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
        </button>
      </div>

      <section
        v-if="timePickerOpen"
        :id="`${id}-time-picker`"
        class="receipt-time-picker"
        role="dialog"
        :aria-label="t('timePickerTitle')"
        @click.stop
        @keydown.esc.stop.prevent="closeTimePicker"
      >
        <header>
          <strong>{{ t('timePickerTitle') }}</strong>
          <output>{{ selectedTime }}</output>
        </header>
        <div class="receipt-time-picker-selects">
          <label>
            <span>{{ t('hours') }}</span>
            <select ref="hourSelect" v-model="selectedHour">
              <option v-for="value in hours" :key="value" :value="value">{{ value }}</option>
            </select>
          </label>
          <span aria-hidden="true">:</span>
          <label>
            <span>{{ t('minutes') }}</span>
            <select v-model="selectedMinute">
              <option v-for="value in minutes" :key="value" :value="value">{{ value }}</option>
            </select>
          </label>
          <span aria-hidden="true">:</span>
          <label>
            <span>{{ t('seconds') }}</span>
            <select v-model="selectedSecond">
              <option v-for="value in minutes" :key="value" :value="value">{{ value }}</option>
            </select>
          </label>
        </div>
        <footer>
          <button class="button button-secondary" type="button" @click="closeTimePicker">{{ t('cancel') }}</button>
          <button class="button" type="button" @click="applySelectedTime">{{ t('applyTime') }}</button>
        </footer>
      </section>
    </div>

    <small v-if="localInvalid" :id="`${id}-format-error`" class="localized-date-time-error">
      {{ t('invalidReceiptDateTime') }}
    </small>
  </fieldset>
</template>
